/**
 * Accounting > Buying Coach Settings — what the buyer coach asks and how it weighs cash.
 *
 * Greg, 2026-10-09: "we need a way to teach you about cash availability so you can guide.
 * We probably need a UI for Claude procurement guidance settings." These four boxes are
 * the coach's instructions in plain words. Saving one changes the next answer it gives;
 * no developer, no deploy.
 *
 * Limited by name on the server (ap-coach-guidance.js) to Greg, Matt and Mercedes.
 * What is NOT here, on purpose: the coach never states a bank balance, every number it
 * gives comes from a lookup, and a fact is only saved when a person presses Save. Those
 * stay fixed however these boxes are filled in.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { apCoachGuidance } from '../services/api';

const ORDER = ['ask_first', 'ask_every_order', 'cash', 'rules'];
const fmtDay = (t) => {
  const d = new Date(t);
  return isNaN(d) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

function Section({ s, onSaved }) {
  const [text, setText] = useState(s.body);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);
  useEffect(() => { setText(s.body); }, [s.body]);
  const dirty = text.trim() !== (s.body || '').trim();

  const save = async (body) => {
    setBusy(true); setErr(''); setDone(false);
    try {
      const out = await apCoachGuidance('save', { section: s.key, body });
      onSaved(out.sections); setDone(true);
    } catch (e) { setErr(e.message || 'That did not save.'); }
    setBusy(false);
  };

  return (
    <div className="card" style={{ padding: 16, marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy, #00395C)' }}>{s.label}</div>
        <div style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)' }}>
          {s.isDefault ? 'Starting draft. Nobody has changed it yet.'
            : `Last changed by ${s.by ? s.by.split(' ')[0] : 'staff'}, ${fmtDay(s.at)}`}
        </div>
      </div>
      <div style={{ fontSize: 13, color: 'var(--gray-600, #4b5563)', margin: '4px 0 8px', maxWidth: 780 }}>{s.help}</div>
      <textarea className="form-input" value={text} onChange={(e) => { setText(e.target.value); setDone(false); }}
        rows={Math.min(14, Math.max(4, text.split('\n').length + 1))}
        style={{ width: '100%', resize: 'vertical', lineHeight: 1.5, fontSize: 13.5 }} />
      {err && <div style={{ color: '#b42318', fontSize: 12.5, marginTop: 6 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="btn btn-primary" disabled={busy || !dirty} onClick={() => save(text)}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        {dirty && <button className="btn btn-outline" disabled={busy} onClick={() => setText(s.body)}>Undo My Changes</button>}
        {!s.isDefault && !dirty && (
          <button className="btn btn-ghost" disabled={busy} onClick={() => save(s.defaultBody)}>Put The Starting Draft Back</button>
        )}
        {done && !dirty && <span style={{ fontSize: 12.5, color: '#1c7c43' }}>Saved. The coach uses it from its next answer.</span>}
      </div>
    </div>
  );
}

export default function CoachSettings() {
  const [sections, setSections] = useState(null);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try { setSections((await apCoachGuidance('get')).sections); }
    catch (e) { setErr(e.message || 'Could not load the settings.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="page-content">
      <div className="breadcrumb"><span>Accounting</span><span> / </span><span>Buying Coach Settings</span></div>
      <h1 style={{ color: 'var(--navy)', margin: '0 0 6px' }}>Buying Coach Settings</h1>
      <div style={{ fontSize: 13.5, color: 'var(--gray-600, #4b5563)', marginBottom: 14, maxWidth: 800 }}>
        What the coach in Plan an Order asks a buyer, and how it weighs an order against cash.
        Write it the way you would tell a new buyer. A change here takes effect on the coach's
        next answer. Three things stay fixed whatever is written here: it never states a bank
        balance, every number it gives comes from a lookup, and it only saves a fact when a
        person presses Save.
      </div>
      <div style={{ fontSize: 13, color: 'var(--gray-600, #4b5563)', marginBottom: 14, maxWidth: 800 }}>
        The cash read itself comes from <strong>Accounting &gt; Cash Outlook</strong>: enter the bank
        balance there, set the cushion, and list any large one-off money coming in or going out.
      </div>
      {err && <div className="card" style={{ padding: 14, color: '#b42318', marginBottom: 12 }}>{err}</div>}
      {!sections && !err && <div style={{ color: 'var(--gray-500, #6b7280)' }}>Loading…</div>}
      {sections && ORDER.map((k) => sections[k] && <Section key={k} s={sections[k]} onSaved={setSections} />)}
    </div>
  );
}
